import { PlusIcon } from '@heroicons/react/24/solid';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { deleteLottery, getLotteries, updateLottery } from '@/api/Lottery';
import { Layout } from '@/components/Layout';
import { LayoutCard } from '@/components/LayoutCard';
import AccentTitle from '@/components/UI/AccentTitle';
import Button from '@/components/UI/Button';
import Confirm from '@/components/UI/Modal/Confirm';
import ReactTable from '@/components/UI/ReactTable';
import type { Lottery } from '@/types/Lottery';
import { getColumns } from './columns';

const LotteryList: React.FC = () => {
  const [lotteries, setLotteries] = useState<Lottery[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedLotteryId, setSelectedLotteryId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLotteries = async () => {
      try {
        const response = await getLotteries();
        setLotteries(response.data.data);
      } catch (error) {
        console.error('Failed to fetch lotteries', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLotteries();
  }, []);

  const handleCreateLottery = () => {
    navigate('/app/lottery/create');
  };

  const handleView = useCallback(
    (id: string) => {
      navigate(`/app/lottery/${id}`);
    },
    [navigate]
  );

  const handleDelete = useCallback((id: string) => {
    setSelectedLotteryId(id);
    setShowConfirm(true);
  }, []);

  const handleUpdateDate = useCallback(async (id: string, field: 'startDate' | 'endDate', value: string) => {
    try {
      await updateLottery(id, { [field]: value });
      // Update the local state to reflect the change
      setLotteries((prevLotteries) => prevLotteries.map((lottery) => (lottery.id === id ? { ...lottery, [field]: value } : lottery)));
    } catch (error) {
      console.error('Failed to update lottery date', error);
    }
  }, []);

  const onConfirmDelete = async () => {
    if (selectedLotteryId) {
      try {
        await deleteLottery(selectedLotteryId);
        setLotteries(lotteries.filter((l) => l.id !== selectedLotteryId));
      } catch (error) {
        console.error('Failed to delete lottery', error);
      } finally {
        setShowConfirm(false);
        setSelectedLotteryId(null);
      }
    }
  };

  const onCancelDelete = () => {
    setShowConfirm(false);
    setSelectedLotteryId(null);
  };

  const columns = useMemo(() => getColumns(handleView, handleDelete, handleUpdateDate), [handleView, handleDelete, handleUpdateDate]);

  return (
    <Layout>
      <LayoutCard>
        <div className='flex justify-between items-center p-4'>
          <AccentTitle title='Lotteries' />
          <Button
            label='Create Lottery'
            btnStyle='btn-primary'
            btnSize='md'
            icon={<PlusIcon className='h-5 w-5' />}
            onClick={handleCreateLottery}
          />
        </div>
        <div className='flex flex-col gap-2 p-4 border-t'>
          <div className='mt-4'>
            <div className='overflow-x-auto'>
              <ReactTable cols={columns} data={lotteries} fetching={isLoading} tableClassName='min-w-full' enableRowSelection={false} />
            </div>
          </div>
        </div>
      </LayoutCard>
      {showConfirm && (
        <Confirm
          title='Delete Lottery'
          message='Are you sure you want to delete this lottery?'
          accept={onConfirmDelete}
          decline={onCancelDelete}
          acceptButtonLabel='Yes'
          declineButtonLabel='No'
        />
      )}
    </Layout>
  );
};

export default LotteryList;
